<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'

import StreamService from '@/services/StreamService'
import { showModal } from '@/Utils/modalBus'
import { APP_EVENTS, emitAppEvent, onAppEvent } from '@/Utils/appEventBus'
import { bytes as formatBytes, duration as formatDuration, cleanMediaName } from '@/filtersNew'

import FormButton from '@/components/Atoms/FormButton.vue'
import AddWithFeedback from '@/components/Molecules/AddWithFeedback.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconChevronRightTiny from '@/components/icon/IconChevronRightTiny.vue'
import IconChevronDownTiny from '@/components/icon/IconChevronDownTiny.vue'
import IconFolderOutline from '@/components/icon/IconFolderOutline.vue'
import IconWarningTriangleFilled from '@/components/icon/IconWarningTriangleFilled.vue'
import IconFileDocument from '@/components/icon/IconFileDocument.vue'
import IconPlaySolid from '@/components/icon/IconPlaySolid.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import Badge from '@/components/Badge.vue'
import Spinner from '@/components/ui/Spinner.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import PlayoutVideoPreviewModal from '@/components/modals/PlayoutVideoPreviewModal.vue'
import PlayoutPagination from './PlayoutPagination.vue'

const FILE_PAGE_SIZE = 8

const props = defineProps({
	dir: {
		type: Object,
		required: true,
	},
	playoutId: {
		type: String,
		default: null,
	},
	isLiveToVod: {
		type: Boolean,
		default: false,
	},
	// When true, hides the per-file delete action (e.g. in the "Add Source"
	// selection modal where deleting files would be out of place).
	hideDelete: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['add-file', 'add-all', 'files-count'])

const store = useStore()
const { notify } = useNotification()

let unsubscribeVideoUploaded = null

const expanded = ref(false)
const loadingFiles = ref(false)
const files = ref([])
const filePage = ref(1)
const selectedVideo = ref(null)
const previewOpen = ref(false)

const playout = computed(() => store.getters['Streams/tvPlayout'])

const fileTotalPages = computed(() =>
	Math.max(1, Math.ceil(files.value.length / FILE_PAGE_SIZE)),
)
const pagedFiles = computed(() => {
	const start = (filePage.value - 1) * FILE_PAGE_SIZE
	return files.value.slice(start, start + FILE_PAGE_SIZE)
})

// Keep the file page within range when the list shrinks (e.g. after a delete).
watch(fileTotalPages, (total) => {
	if (filePage.value > total) filePage.value = total
})

const playlist = computed(() => store.getters['Playout/playlist'] || [])

const maxUploadFileBytes = computed(() => {
	const gb = store.getters['User/tvPlayoutMaxUploadFileGB'] || 0
	return gb ? gb * (1000 ** 3) : 0
})

const maxFileDurationSec = computed(() => {
	const min = store.getters['User/tvPlayoutMaxUploadFileDurationMin'] || 0
	return min ? min * 60 : 0
})

const isFileSizeLimitExceeded = (file) => {
	if (props.isLiveToVod || !maxUploadFileBytes.value || !file.bytes) return false
	return file.bytes > maxUploadFileBytes.value
}

const isFileDurationLimitExceeded = (file) => {
	if (props.isLiveToVod || !maxFileDurationSec.value || !file.mediaInfo?.duration) return false
	return file.mediaInfo.duration > maxFileDurationSec.value
}

const isFileLimitExceeded = (file) =>
	isFileSizeLimitExceeded(file) || isFileDurationLimitExceeded(file)

const hasLimitedFiles = computed(() => {
	if (props.isLiveToVod) return false
	return files.value.some((file) => isFileLimitExceeded(file))
})

const filesCount = computed(() => {
	if (props.isLiveToVod) return props.dir?.totalFiles ?? 0
	return files.value?.length ?? 0
})

const filesLabel = computed(() => {
	const count = filesCount.value
	return `${count} ${count === 1 ? 'file' : 'files'}`
})

// Report this directory's file count up to the parent so the summary
// ("X files in Y directories") stays in sync after uploads/deletions.
const dirKey = computed(() => props.dir?.id || props.dir?._id || props.dir?.name)
watch(
	filesCount,
	(count) => emit('files-count', { id: dirKey.value, count }),
	{ immediate: true },
)

const loadFiles = async () => {
	if (!props.playoutId || props.isLiveToVod) return
	loadingFiles.value = true
	try {
		const data = await StreamService.listPlayoutDirFiles(props.playoutId, props.dir.id)
		files.value = Array.isArray(data) ? data : []
		filePage.value = 1
	} catch (e) {
		files.value = []
	} finally {
		loadingFiles.value = false
	}
}

const loadLiveToVodFiles = async () => {
	if (!props.dir?._id) return
	loadingFiles.value = true
	try {
		await store.dispatch('Streams/getVodFilesByVodId', props.dir._id)
		files.value = store.getters['Streams/vodFiles'] || []
		filePage.value = 1
	} catch (e) {
		files.value = []
	} finally {
		loadingFiles.value = false
	}
}

const onToggle = async () => {
	expanded.value = !expanded.value
	if (expanded.value && files.value.length === 0) {
		if (props.isLiveToVod) {
			await loadLiveToVodFiles()
		} else {
			await loadFiles()
		}
	}
}

// Reload this directory's files when a video finishes uploading, so newly
// uploaded files appear without a manual refresh.
const onVideoUploaded = async () => {
	if (props.isLiveToVod) return
	await loadFiles()
}

onMounted(async () => {
	// Pre-load count for upload-dir folders so the badge can show file count
	if (!props.isLiveToVod && props.playoutId) {
		await loadFiles()
	}
	unsubscribeVideoUploaded = onAppEvent(APP_EVENTS.VIDEO_UPLOADED, onVideoUploaded)
})

onUnmounted(() => {
	unsubscribeVideoUploaded?.()
})

const fileAddCount = (file) => {
	const id = file.id || file._id
	if (!id) return 0
	return playlist.value.filter(
		(item) => item.sourceLocation === id || item.sourceLocation === file.refId,
	).length
}

// Total schedule entries for files currently loaded in this folder.
const folderAddCount = computed(() =>
	files.value.reduce((sum, file) => sum + fileAddCount(file), 0),
)

const isFileProcessing = (file) => {
	if (file.statusProcessing) return true
	if (file.uploading) return true
	if (!file.mediaInfo) return true
	return false
}

// Under 1h: mm:ss (e.g. 00:15). At/over 1h: hh:mm:ss (e.g. 12:27:00).
// trim:false keeps leading zeros so sub-minute clips don't collapse to "15".
const fileDurationLabel = (file) => {
	const sec = file.mediaInfo?.duration
	if (!sec) return ''
	const ms = sec * 1000
	return formatDuration(ms, ms >= 3600000 ? 'hh:mm:ss' : 'mm:ss', false)
}

const fileMetaLine = (file) => {
	const parts = []
	if (file.mediaInfo?.duration && !isFileDurationLimitExceeded(file)) {
		parts.push(fileDurationLabel(file))
	}
	if (file.bytes && !isFileSizeLimitExceeded(file)) {
		parts.push(formatBytes(file.bytes, false, 1, true))
	}
	return parts.join(' • ')
}

const fileSizeLabel = (file) => formatBytes(file.bytes, false, 1, true)

const formatUsage = (value) => {
	const rounded = Math.round((value || 0) * 100) / 100
	return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0$/, '')
}

const maxUploadFileLabel = computed(() => {
	const gb = store.getters['User/tvPlayoutMaxUploadFileGB'] || 0
	if (!gb) return ''
	return gb >= 1 ? `${formatUsage(gb)} GB` : `${formatUsage(gb * 1000)} MB`
})

const maxFileDurationLabel = computed(() => {
	const min = store.getters['User/tvPlayoutMaxUploadFileDurationMin'] || 0
	return min ? `${formatUsage(min)} min` : ''
})

// Red caption shown under a file that breaks one or both per-file plan limits.
const fileLimitCaption = (file) => {
	const sizeBad = isFileSizeLimitExceeded(file)
	const durationBad = isFileDurationLimitExceeded(file)
	if (sizeBad && durationBad) return 'Exceeds maximum file size & duration limit'
	if (sizeBad) return 'Exceeds maximum file size'
	if (durationBad) return 'Exceeds maximum duration'
	return ''
}

const isFileAddable = (file) => {
	if (isFileProcessing(file)) return false
	if (isFileLimitExceeded(file)) return false
	const codecOk =
		file.mediaInfo?.codecs?.find((c) => c.codec === 'AVC') || props.isLiveToVod
	if (!codecOk) return false
	const tooBig = file.bytes > 10737418240
	const tooLargeRes =
		file.mediaInfo?.width > 1920 || file.mediaInfo?.height > 1080
	return !tooBig && !tooLargeRes
}

const fileDisabledReason = (file) => {
	if (isFileProcessing(file)) return 'Still processing. Please wait until the file is ready.'
	const sizeBad = isFileSizeLimitExceeded(file)
	const durationBad = isFileDurationLimitExceeded(file)
	if (sizeBad && durationBad)
		return `This file exceeds the maximum file size of ${maxUploadFileLabel.value} and the maximum duration of ${maxFileDurationLabel.value}. Remove it or upgrade your plan to add it.`
	if (sizeBad)
		return `This file exceeds the maximum file size of ${maxUploadFileLabel.value}. Remove it or upgrade your plan to add it.`
	if (durationBad)
		return `This file exceeds the maximum duration of ${maxFileDurationLabel.value}. Remove it or upgrade your plan to add it.`
	const codecOk =
		file.mediaInfo?.codecs?.find((c) => c.codec === 'AVC') || props.isLiveToVod
	if (!codecOk) return 'Only H.264 (AVC) videos can be added.'
	if (file.bytes > 10737418240) return 'File exceeds the 10 GB limit.'
	if (file.mediaInfo?.width > 1920 || file.mediaInfo?.height > 1080)
		return 'Resolution exceeds 1080p limit.'
	return ''
}

const addFile = (file) => {
	emit('add-file', { ...file, _playoutUpload: !props.isLiveToVod })
}

const requestVideoPreview = (file) => {
	const id = file.id || file._id
	if (!id || !playout.value) return
	selectedVideo.value = {
		...file,
		id,
		_id: file._id || id,
		fileName: file.fileName || file.name,
		size: file.size || file.bytes,
		type: file.type || (props.isLiveToVod ? 'live-to-vod' : 'playout-upload'),
	}
	previewOpen.value = true
}

const addAll = async () => {
	if (!files.value.length) {
		if (props.isLiveToVod) {
			await loadLiveToVodFiles()
		} else {
			await loadFiles()
		}
	}
	const eligible = files.value.filter((f) => isFileAddable(f))
	if (eligible.length === 0) {
		notify({ group: 'info', text: 'No files ready to schedule in this folder' })
		return false
	}
	eligible.forEach((file) => emit('add-file', { ...file, _playoutUpload: !props.isLiveToVod }))
	return true
}

const deleteModalId = computed(
	() => 'playout-v2-delete-file-' + (props.dir?.id || props.dir?._id || ''),
)
const inUseModalId = computed(
	() => 'playout-v2-file-in-use-' + (props.dir?.id || props.dir?._id || ''),
)
const pendingDeleteFile = ref(null)
const deleting = ref(false)

const requestDeleteFile = (file) => {
	if (props.isLiveToVod) return
	pendingDeleteFile.value = file
	// A file still referenced by the schedule (playlist) can't be deleted — send
	// the user to the Schedule tab to remove it there first.
	if (fileAddCount(file) > 0) {
		showModal(inUseModalId.value)
	} else {
		showModal(deleteModalId.value)
	}
}

// "View playlist" action from the in-use modal: switch the playout view to its
// Schedule tab so the source can be removed.
const viewPlaylist = () => {
	emitAppEvent(APP_EVENTS.PLAYOUT_VIEW_SCHEDULE)
	pendingDeleteFile.value = null
}

const confirmDeleteFile = async () => {
	const file = pendingDeleteFile.value
	if (!file || props.isLiveToVod || deleting.value) return
	deleting.value = true
	const fileId = file.id || file._id
	try {
		await StreamService.deletePlayoutDirFile(
			props.playoutId,
			props.dir.id || props.dir._id,
			fileId,
			file,
		)
		// Remove from UI immediately
		files.value = files.value.filter((f) => (f.id || f._id) !== fileId)
		// Notify the page so it can refresh directory stats and the storage/
		// duration usage totals to reflect the deletion.
		emitAppEvent(APP_EVENTS.PLAYOUT_FILE_REMOVED)
		notify({ group: 'success', text: `${file.fileName || file.name || 'File'} deleted` })
	} catch (e) {
		notify({ group: 'error', text: e?.message || 'Failed to delete file' })
	} finally {
		deleting.value = false
		pendingDeleteFile.value = null
	}
}

</script>

<template>
	<div class="py-2 min-w-0 max-w-full">
		<div class="flex items-center gap-3 min-w-0 max-w-full">
			<button
				type="button"
				class="shrink-0 text-surface-7 hover:text-white transition-colors p-1"
				:aria-label="expanded ? 'Collapse' : 'Expand'"
				@click="onToggle"
			>
			<icon-base v-if="!expanded" :width="6" :height="11" width-view-box="6" height-view-box="11"><icon-chevron-right-tiny /></icon-base>
			<icon-base v-else :width="11" :height="6" width-view-box="11" height-view-box="6"><icon-chevron-down-tiny /></icon-base>
		</button>

		<div class="shrink-0 w-8 h-8 rounded-lg bg-surface-1 flex items-center justify-center"
			:class="isLiveToVod ? 'text-main' : 'text-surface-8'">
			<icon-base :width="17" :height="17" width-view-box="24" height-view-box="24" fill="none"><icon-folder-outline /></icon-base>
		</div>

			<div class="flex flex-col min-w-0 flex-1 overflow-hidden gap-0.5">
				<div class="flex items-center gap-2 min-w-0 overflow-hidden">
					<span class="text-sm text-white truncate min-w-0 flex-1" :title="dir.name">{{ dir.name }}</span>
					<Tooltip
						v-if="hasLimitedFiles"
						content="Some files exceed your plan limits"
						wrapperClass="inline-flex shrink-0"
						popperClass="max-w-[220px] text-sm"
					>
						<icon-base class="shrink-0 text-[#E25858]" aria-label="Some files exceed your plan limits"><icon-warning-triangle-filled /></icon-base>
					</Tooltip>
					<Badge
						v-if="isLiveToVod"
						type="main"
						inline
						pill
						size="sm"
						class="shrink-0 whitespace-nowrap max-lg:hidden"
					>
						Live recording
					</Badge>
				</div>
				<span class="text-tiny text-surface-7 lg:hidden">{{ filesLabel }}</span>
			</div>

			<AddWithFeedback
				v-if="filesCount > 0"
				success-label="Added to schedule"
				:count="folderAddCount"
				:show-count="false"
				:action="addAll"
			>
				<span class="max-lg:hidden">Add all contents</span>
				<span class="lg:hidden">Add all</span>
			</AddWithFeedback>

			<span class="text-tiny text-surface-7 shrink-0 min-w-[60px] text-right max-lg:hidden">{{ filesLabel }}</span>
		</div>

		<div v-if="expanded" class="mt-3 pl-2 md:pl-12 min-w-0">
			<Spinner v-if="loadingFiles" class="my-2" />
			<div v-else-if="!files.length" class="text-tiny text-surface-7 py-2">
				This folder is empty.
			</div>
			<ul v-else class="divide-y divide-surface-3 min-w-0">
				<li
					v-for="file in pagedFiles"
					:key="file.id || file._id"
					class="group flex items-center gap-3 py-2 px-2 -mx-2 rounded-md hover:bg-surface-1 transition-colors min-w-0"
				>
					<icon-base class="shrink-0 text-dark-8" :width="18" :height="17" width-view-box="18" height-view-box="17"><icon-file-document /></icon-base>

					<div class="flex-1 min-w-0">
						<p class="text-sm text-white m-0 truncate" :title="file.fileName || file.name">
							{{ cleanMediaName(file.fileName || file.name) }}
						</p>
						<div v-if="isFileProcessing(file)" class="flex items-center gap-2 text-tiny text-surface-7 mt-0.5">
							<Spinner spinner-size="10px" spinner-color="var(--c-dark-8)" classes="!gap-0" />
							<span>Processing… may take a few minutes</span>
						</div>
						<div v-else class="flex items-center gap-1.5 mt-0.5 min-w-0">
							<Badge
								v-if="fileAddCount(file) > 0"
								type="enabled"
								size="xs"
								pill
								inline
								class="!px-2 !py-px h-4 shrink-0 whitespace-nowrap gap-1"
							>
								<icon-base :width="10" :height="10">
									<icon-checkmark />
								</icon-base>
								{{ fileAddCount(file) === 1 ? 'In schedule' : `In schedule · ${fileAddCount(file)}x` }}
							</Badge>
							<p v-if="!isFileLimitExceeded(file)" class="text-tiny text-surface-7 m-0 truncate">{{ fileMetaLine(file) }}</p>
							<p v-else class="text-tiny m-0 flex flex-wrap items-center gap-x-1 min-w-0">
								<template v-if="file.mediaInfo?.duration">
									<span :class="isFileDurationLimitExceeded(file) ? 'text-error' : 'text-surface-7'">{{ fileDurationLabel(file) }}</span>
									<span class="text-surface-7">•</span>
								</template>
								<template v-if="file.bytes">
									<span :class="isFileSizeLimitExceeded(file) ? 'text-error' : 'text-surface-7'">{{ fileSizeLabel(file) }}</span>
									<span class="text-surface-7">•</span>
								</template>
								<span class="text-error">{{ fileLimitCaption(file) }}</span>
							</p>
						</div>
					</div>

					<FormButton
						:disabled="isFileProcessing(file)"
						customClasses="rounded-full !bg-surface-3 hover:!bg-surface-4 text-surface-text-1 hover:text-white size-7 inline-flex items-center justify-center shrink-0 disabled:opacity-50 disabled:pointer-events-none"
						@click="requestVideoPreview(file)"
					>
						<icon-base class="-translate-x-px" :width="16" :height="16">
							<icon-play-solid />
						</icon-base>
					</FormButton>

					<AddWithFeedback
						success-label="Added to schedule"
						label="Add"
						:count="fileAddCount(file)"
						:show-count="false"
						:disabled="!isFileAddable(file)"
						:disabled-tooltip="fileDisabledReason(file)"
						@add="addFile(file)"
					/>

					<FormButton
						v-if="!isLiveToVod && !hideDelete"
						type="link"
						isIcon
						size="sm"
						class="text-surface-7 hover:text-error-light lg:opacity-0 group-hover:opacity-100 transition-opacity"
						@click="requestDeleteFile(file)"
					>
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
				</li>
			</ul>
			<PlayoutPagination v-model="filePage" :total-pages="fileTotalPages" />
		</div>

		<ConfirmModal
			:modalId="deleteModalId"
			title="Delete video?"
			message="Are you sure you want to delete this video? This action cannot be undone."
			modal-type="danger"
			okText="Delete"
			cancelText="Cancel"
			@modal-confirm="confirmDeleteFile"
			@modal-cancel="pendingDeleteFile = null"
		/>

		<ConfirmModal
			:modalId="inUseModalId"
			title="Video is in use"
			message="This video can't be deleted because it's currently added to a TV Playout playlist. Remove it from the playlist before deleting it."
			modal-type="main"
			okText="View playlist"
			cancelText="Cancel"
			@modal-confirm="viewPlaylist"
			@modal-cancel="pendingDeleteFile = null"
		/>

		<PlayoutVideoPreviewModal
			v-if="selectedVideo && playout"
			v-model="previewOpen"
			:stream="playout"
			:video="selectedVideo"
		/>
	</div>
</template>

<style scoped></style>
